import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import Iframe from "react-iframe";
import {
  FaArrowLeft,
  FaFileAlt,
  FaSyncAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVER_NAME = process.env.REACT_APP_SERVER_NAME;
const SYSTEM_CODE = process.env.REACT_APP_SYSTEM_CODE;
const TARGET_DIR_PRINTABLE = process.env.REACT_APP_TARGET_DIR_PRINTABLE;
const DEFAULT_PRINTABLE_FILE =
  "QuestionPaper/question_paper_eduok_modern.php?";

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const DEFAULT_IFRAME_HEIGHT = "480px";
const DEFAULT_DOCUMENT_TYPE = "question_paper";

//Created By Cursor
function decodeParam(value) {
  if (value == null || value === "") return "";
  try {
    return decodeURIComponent(String(value));
  } catch {
    return String(value);
  }
}

//Created By Cursor
export default function PrintQuestionPaper() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;
  const params = useParams();
  const documentType =
    decodeParam(params.document_type) || DEFAULT_DOCUMENT_TYPE;
  const paperId = decodeParam(params.paper_id || params.id);

  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paper, setPaper] = useState(null);
  const [samples, setSamples] = useState([]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
  const [fileUrl, setFileUrl] = useState("");
  const loadSeqRef = useRef(0);

  const selectedSample = samples[sampleIndex] || null;

  //Created By Cursor — printable URL (same pattern as PayRollPayslip / Acknowledgement)
  const buildPreviewUrl = useCallback(
    (id, sample, sessionId = "") => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !id) return "";

      const base =
        sample?.file_directory && sample?.file_name
          ? `${SERVER_NAME}${sample.file_directory}${sample.file_name}`
          : `${SERVER_NAME}${TARGET_DIR_PRINTABLE}${DEFAULT_PRINTABLE_FILE}`;

      const joiner = base.includes("?") ? "&" : "?";
      const sampleId = sample?.sample_id || "";
      const colorOptions = Array.isArray(sample?.color_options)
        ? sample.color_options
        : [];
      const colorOptionId = colorOptions[0]?.color_option_id || "";

      return (
        `${base}${joiner}` +
        `paper_id=${encodeURIComponent(id)}` +
        `&id=${encodeURIComponent(id)}` +
        `&sample_id=${encodeURIComponent(sampleId)}` +
        `&session_id=${encodeURIComponent(sessionId || "")}` +
        `&document_type=${encodeURIComponent(documentType)}` +
        `&template=eduok_modern` +
        `&system_code=${encodeURIComponent(SYSTEM_CODE || "")}` +
        `&color_option_id=${encodeURIComponent(colorOptionId)}` +
        `&school_code=${encodeURIComponent(schoolCode)}`
      );
    },
    [documentType]
  );

  //Created By Cursor
  const applyPreview = useCallback(
    (id, list, nextSampleIndex, sessionId = "") => {
      const sample = list?.[nextSampleIndex];
      const url = buildPreviewUrl(id, sample, sessionId);
      setPreviewLoading(Boolean(url));
      setFileUrl(url);
      console.log("[PrintQuestionPaper] FILE_URL", url);
    },
    [buildPreviewUrl]
  );
  const applyPreviewRef = useRef(applyPreview);
  applyPreviewRef.current = applyPreview;

  //Created By Cursor — load once per paperId/documentType
  useEffect(() => {
    window.scrollTo(0, 0);
    const seq = ++loadSeqRef.current;
    const schoolCode = SessionManager.getSchoolCode();

    setPaper(null);
    setSamples([]);
    setSampleIndex(0);
    setFileUrl("");
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    setPreviewLoading(false);

    if (!schoolCode || !paperId) {
      myToast.notify_warning("Paper id or school code is missing.");
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    (async () => {
      try {
        const [paperRes, sampleRes] = await Promise.all([
          axios.get(`${API_BASE()}getQuestionPaper.php`, {
            params: { school_code: schoolCode, id: paperId },
          }),
          axios.get(`${API_BASE()}getPrintableSample.php`, {
            params: {
              school_code: schoolCode,
              document_type: documentType,
            },
          }),
        ]);

        if (seq !== loadSeqRef.current) return;

        if (paperRes.data?.api_status === "OK" && paperRes.data.data) {
          const paperData = paperRes.data.data;
          setPaper(paperData);

          let list = [];
          if (
            sampleRes.data?.api_status === "OK" &&
            Array.isArray(sampleRes.data.data)
          ) {
            list = sampleRes.data.data;
          } else if (sampleRes.data?.api_remark) {
            myToast.notify_warning(sampleRes.data.api_remark);
          }
          setSamples(list);
          setSampleIndex(0);
          applyPreviewRef.current(
            paperId,
            list,
            0,
            paperData.session_id || ""
          );
        } else {
          setPaper(null);
          setFileUrl("");
          myToast.notify_warning(
            paperRes.data?.api_remark ||
              paperRes.data?.message ||
              "Paper not found."
          );
        }
      } catch (err) {
        if (seq !== loadSeqRef.current) return;
        console.error("[PrintQuestionPaper] load error:", err);
        setPaper(null);
        setFileUrl("");
        myToast.notify_warning("Failed to load question paper.");
      } finally {
        if (seq === loadSeqRef.current) {
          setLoading(false);
        }
      }
    })();

    return undefined;
  }, [paperId, documentType]);

  //Created By Cursor — resize when embedded document posts { iframeHeight }
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.iframeHeight) {
        setIframeHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage, false);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  //Created By Cursor
  useEffect(() => {
    if (!fileUrl) return;
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    setPreviewLoading(true);
  }, [fileUrl]);

  //Created By Cursor
  const handleSampleSelect = useCallback(
    (index) => {
      setSampleIndex(index);
      applyPreview(paperId, samples, index, paper?.session_id || "");
    },
    [paperId, samples, paper, applyPreview]
  );

  const metaLine = useMemo(() => {
    if (!paper) return "";
    const classLabel = paper.class_name || paper.class_id;
    const subjectLabel = paper.subject_name || paper.subject_id;
    const parts = [
      paper.exam_name,
      classLabel ? `Class ${classLabel}` : "",
      subjectLabel ? `Subject ${subjectLabel}` : "",
      paper.max_marks != null ? `${paper.max_marks} marks` : "",
    ].filter(Boolean);
    return parts.join(" · ");
  }, [paper]);

  const documentCaption =
    selectedSample?.document_caption ||
    selectedSample?.sample_name ||
    "Question Paper";

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page pq-page ack-page"
      style={{ background: "#f5f7fb", fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="pq-crumb"
                onClick={() => history.push("/AdminMain/QuestionPapers")}
              >
                Question Paper
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Print</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaFileAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Preview Question Paper</h1>
              <p className="sm-hero__sub">
                document_type · {documentType}
                {metaLine ? ` · ${metaLine}` : ""}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="pq-btn pq-btn--ghost"
              onClick={() =>
                history.push(
                  paperId
                    ? `/AdminMain/PaperBuilder/${paperId}`
                    : "/AdminMain/PaperBuilder"
                )
              }
            >
              <FaArrowLeft />
              <span>Back to builder</span>
            </button>
            <button
              type="button"
              className={`pq-icon-btn${previewLoading ? " is-spinning" : ""}`}
              title="Refresh preview"
              disabled={loading || previewLoading}
              onClick={() => {
                applyPreview(
                  paperId,
                  samples,
                  sampleIndex,
                  paper?.session_id || ""
                );
              }}
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="sr-body ack-body pq-body">
          {loading ? (
            <div className="pq-loading">
              <MyLoader />
              <p>Loading question paper…</p>
            </div>
          ) : !paper ? (
            <div className="ack-preview__empty">
              Question paper not found for this id.
            </div>
          ) : (
            <>
              <section className="pq-meta-card" aria-label="Paper summary">
                <div className="pq-meta-card__icon" aria-hidden>
                  <FaFileAlt />
                </div>
                <div className="min-w-0">
                  <p className="pq-meta-card__eyebrow">
                    document_type · {documentType}
                  </p>
                  <strong className="pq-meta-card__title">
                    {paper.exam_name || "Question paper"}
                  </strong>
                  <div className="pq-meta-card__row">
                    <span>Paper #{paper.id}</span>
                    {paper.class_name || paper.class_id ? (
                      <span>Class {paper.class_name || paper.class_id}</span>
                    ) : null}
                    {paper.subject_name || paper.subject_id ? (
                      <span>
                        Subject {paper.subject_name || paper.subject_id}
                      </span>
                    ) : null}
                    <span>{paper.max_marks} marks</span>
                    {paper.duration_minutes ? (
                      <span>{paper.duration_minutes} min</span>
                    ) : null}
                    <span className="pq-status">
                      {String(paper.status || "draft")}
                    </span>
                  </div>
                </div>
              </section>

              {samples.length > 1 ? (
                <section className="ack-picker" aria-label="Template">
                  <div className="ack-picker__head">
                    <FaFileAlt aria-hidden />
                    <span>Select template</span>
                  </div>
                  <div className="ack-samples">
                    {samples.map((item, index) => (
                      <button
                        key={item.sample_id || index}
                        type="button"
                        className={`ack-sample${
                          sampleIndex === index ? " is-active" : ""
                        }`}
                        onClick={() => handleSampleSelect(index)}
                      >
                        {item.sample_img ? (
                          <img src={item.sample_img} alt="" />
                        ) : (
                          <span className="ack-sample__fallback" aria-hidden>
                            <FaFileAlt />
                          </span>
                        )}
                        <span>
                          {item.sample_name ||
                            item.document_caption ||
                            `Sample ${index + 1}`}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="ack-preview" aria-label="Preview">
                <div className="ack-preview__head">
                  <h2>Preview</h2>
                  <span className="ack-preview__meta">
                    {documentCaption} · use Print in preview
                  </span>
                </div>
                <div className="ack-preview__frame">
                  {previewLoading ? (
                    <div className="ack-preview__loader">
                      <MyLoader />
                    </div>
                  ) : null}
                  {fileUrl ? (
                    <div key={fileUrl} className="ack-iframe-wrap">
                      <Iframe
                        url={fileUrl}
                        width="100%"
                        height={iframeHeight}
                        overflow="hidden"
                        scrolling="no"
                        id="iFrameQuestionPaper"
                        className="ack-iframe"
                        display="initial"
                        position="relative"
                        onLoad={() => setPreviewLoading(false)}
                      />
                    </div>
                  ) : (
                    <div className="ack-preview__empty">
                      No preview available for this question paper.
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
